/**
 * Fallback-label select sentinels and payload mapping.
 *
 * "Best guess" is an explicit choice (the former blank/optional state): the
 * model always returns its top prediction and no fallback label is stored.
 * "No predictions" keeps confidence rejection on with `rejection_label_name`
 * cleared, so low-confidence examples produce an empty prediction.
 */

export const NO_PREDICTIONS_VALUE = '__no_predictions__';
export const BEST_GUESS_VALUE = '__best_guess__';

export const NO_PREDICTIONS_LABEL = 'No predictions';
export const BEST_GUESS_LABEL = 'Best guess';

export const BEST_GUESS_TOOLTIP =
    "Use the model's best prediction, even when confidence is very low. No fallback label is used.";

export const NO_PREDICTIONS_TOOLTIP =
    "Return no prediction when the model isn't confident enough.";

export const isFallbackSentinel = (value) =>
    value === BEST_GUESS_VALUE || value === NO_PREDICTIONS_VALUE;

/** Stored `rejection_label_name` for a select value. Sentinels map to null. */
export const rejectionLabelNameFromFallback = (fallbackLabel) => {
    if (!fallbackLabel || isFallbackSentinel(fallbackLabel)) return null;
    return fallbackLabel;
};

/**
 * Single-label confidence rejection is on for any real fallback choice
 * ("No predictions" or a label) and off for Best guess / blank.
 */
export const confidenceRejectionFromFallback = (fallbackLabel, classificationType) =>
    classificationType === 'single' &&
    Boolean(fallbackLabel) &&
    fallbackLabel !== BEST_GUESS_VALUE;

export const fallbackPayloadFromSelect = (
    fallbackLabel,
    { isClassification, classificationType } = {}
) => {
    if (!isClassification) {
        return { confidence_rejection_enabled: false, rejection_label_name: null };
    }
    return {
        confidence_rejection_enabled: confidenceRejectionFromFallback(
            fallbackLabel,
            classificationType
        ),
        rejection_label_name: rejectionLabelNameFromFallback(fallbackLabel),
    };
};

/**
 * Map stored usecase fields back onto the select. A single-label task with
 * rejection disabled (the former blank select) becomes Best guess.
 */
export const fallbackLabelFromStored = ({
    isClassification,
    isMultilabel,
    rejectionLabelName,
    confidenceRejectionEnabled,
} = {}) => {
    if (!isClassification) return null;
    if (rejectionLabelName) return rejectionLabelName;
    if (isMultilabel) return NO_PREDICTIONS_VALUE;
    if (confidenceRejectionEnabled === false) return BEST_GUESS_VALUE;
    return NO_PREDICTIONS_VALUE;
};
